import * as React from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DrillEntry {
  label: string;
  onBack: () => void;
}

interface MobileNavDrillContextValue {
  push: (label: string, onBack: () => void) => void;
  pop: () => void;
  top: DrillEntry | null;
}

// ─── Context ──────────────────────────────────────────────────────────────────

export const MobileNavDrillContext = React.createContext<MobileNavDrillContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function MobileNavDrillProvider({ children }: { children: React.ReactNode }) {
  const [stack, setStack] = React.useState<DrillEntry[]>([]);

  const push = React.useCallback((label: string, onBack: () => void) => {
    setStack((s) => [...s, { label, onBack }]);
  }, []);

  const pop = React.useCallback(() => {
    setStack((s) => s.slice(0, -1));
  }, []);

  const top = stack[stack.length - 1] ?? null;

  const value = React.useMemo(() => ({ push, pop, top }), [push, pop, top]);

  return <MobileNavDrillContext.Provider value={value}>{children}</MobileNavDrillContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Returns the topmost drill entry (label + onBack), or null when at root. */
export function useMobileNavDrillTop(): DrillEntry | null {
  return React.useContext(MobileNavDrillContext)?.top ?? null;
}
